import { DateTime } from "luxon";

const PREDEFINED_TIMEZONE = "America/New_York";

// Custom DateTime Type
export class SchoolTime {
  private dateTime: DateTime;

  /*
  constructor(p: string | SchoolTime | Date | DateTime) {
    if (p instanceof SchoolTime) {
      this.dateTime = p.dateTime; // Copy existing instance
    } else if (p instanceof Date) {
      this.dateTime = DateTime.fromJSDate(p).setZone(PREDEFINED_TIMEZONE);
    } else if (p instanceof DateTime) {
      this.dateTime = p;
    } else {
      if (!SchoolTime.isValidISOWithoutTimezone(p)) {
        throw new Error("Invalid ISO format or contains timezone offset: " + p);
      }
      this.dateTime = DateTime.fromISO(p, { zone: PREDEFINED_TIMEZONE });
    }
  }*/

  private constructor(dt: DateTime) {
    this.dateTime = dt;
  }

  static fromSchoolTime(p: SchoolTime): SchoolTime | null | undefined {
    return new SchoolTime(p.dateTime); // Copy existing instance
  }

  static fromJSDate(p: Date | null | undefined): SchoolTime | null | undefined {
    if (p === null || p === undefined) {
      return p as null | undefined;
    }
    return new SchoolTime(DateTime.fromJSDate(p).setZone(PREDEFINED_TIMEZONE));
  }

  static fromDateTime(p: DateTime): SchoolTime | null | undefined {
    if (p === null || p === undefined) {
      return p;
    }
    return new SchoolTime(p.setZone(PREDEFINED_TIMEZONE));
  }

  static fromISO(p: string | null | undefined): SchoolTime | null | undefined {
    if (p === null || p === undefined) {
      return p as null | undefined;
    }
    if (!SchoolTime.isValidISOWithoutTimezone(p)) {
      throw new Error("Invalid ISO format or contains timezone offset: " + p);
    }
    return new SchoolTime(DateTime.fromISO(p, { zone: PREDEFINED_TIMEZONE }));
  }

  static fromISODate(p: string | null | undefined): SchoolTime | null | undefined {
    if (p === null || p === undefined) {
      return p as null | undefined;
    }
    if (!SchoolTime.isValidISODate(p)) {
      throw new Error("Invalid ISO date: " + p);
    }
    return new SchoolTime(DateTime.fromISO(p, { zone: PREDEFINED_TIMEZONE }));
  }

  // Get original ISO string without timezone
  toISOLocal(): string {
    return this.dateTime.toISO()?.slice(0, 19) ?? "";
  }

  // Convert to Luxon DateTime
  toDateTime(): DateTime {
    return this.dateTime;
  }

  // Format for display in UI
  toDisplayFormat(format = "yyyy-MM-dd HH:mm"): string {
    return this.dateTime.toFormat(format);
  }

  // Short date
  toShortDateString(format = "D"): string {
    return this.dateTime.toFormat(format);
  }

  // Convert to JS Date
  toJSDate(): Date {
    return this.dateTime.toJSDate();
  }

  // Create new instances with modifications
  addHours(hours: number): SchoolTime {
    return new SchoolTime(this.dateTime.plus({ hours }));
  }

  addMinutes(minutes: number): SchoolTime {
    return new SchoolTime(this.dateTime.plus({ minutes }));
  }

  addDays(days: number): SchoolTime {
    return new SchoolTime(this.dateTime.plus({ days }));
  }

  addMonths(n: number): SchoolTime {
    return new SchoolTime(this.dateTime.plus({ months: n }));
  }

  setMonth(month: number): SchoolTime {
    return new SchoolTime(this.dateTime.set({ month }));
  }

  setMonthStart(): SchoolTime {
    return new SchoolTime(this.dateTime.startOf("month"));
  }

  // Get date components
  getYear(): number {
    return this.dateTime.year;
  }

  getMonth(): number {
    return this.dateTime.month;
  }

  getDay(): number {
    return this.dateTime.day;
  }

  getDayOfWeek(): number {
    return (this.dateTime.weekday) % 7; // Sunday = 0, Luxon weekday starts at 1
  }

  // Comparison operators
  isBefore(other: SchoolTime): boolean {
    return this.dateTime < other.dateTime;
  }

  isAfter(other: SchoolTime): boolean {
    return this.dateTime > other.dateTime;
  }

  isSameOrBefore(other: SchoolTime): boolean {
    return this.dateTime <= other.dateTime;
  }

  isSameOrAfter(other: SchoolTime): boolean {
    return this.dateTime >= other.dateTime;
  }

  // Returns a new instance with the current time
  static now(): SchoolTime {
    return new SchoolTime(DateTime.now().setZone(PREDEFINED_TIMEZONE));
  }

  // Validate ISO format (YYYY-MM-DDTHH:mm:ss) without timezone
  static isValidISOWithoutTimezone(isoString: string): boolean {
    const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?$/;
    return isoRegex.test(isoString) && !isoString.includes("Z") && !isoString.match(/[-+]\d{2}:\d{2}/);
  }

  // Validate ISO format (YYYY-MM-DDTHH:mm:ss) without timezone
  static isValidISODate(isoString: string): boolean {
    const isoRegex = /^\d{4}-\d{2}-\d{2}$/;
    return isoRegex.test(isoString);
  }
}
